import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  INTEGRATION_CALL_STATUSES,
  type IntegrationCall,
  type IntegrationCallStatus,
} from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Select,
  Table,
} from '@platform/ui';

import { LoadingBlock } from '../../components/data';
import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

import { CALL_STATUS_LABELS, CALL_STATUS_TONES, formatLatency, formatMoment } from './status';

/**
 * The Activity tab: every vendor call this clinic made, redacted (FR-MKT-05, API.md §2.10).
 *
 * The rows are the audit of an integration's behaviour — operation, outcome, how long it
 * took, which error class, when — and deliberately nothing more. `summary` carries counts
 * and sizes; a request or response body never reaches this table, because vendor payloads
 * for an EHR or an eligibility check are PHI (rules §29.5, SECURITY.md §7).
 *
 * Paging is the API's cursor paging and not an offset: `nextCursor` forward, a local stack
 * backward. There is no total, by design — counting every call for a busy clinic is an
 * expensive query for a number nobody acts on (API.md §1.7).
 */

const PAGE_SIZE = 10;

export function ActivityLog({ integrationKey }: { integrationKey: string }) {
  const client = useIntegrationsClient();
  const [status, setStatus] = useState<IntegrationCallStatus | ''>('');
  /* The stack's last entry is the current page; `null` is the first. */
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1] ?? null;

  const calls = useQuery({
    queryKey: ['integration-calls', integrationKey, status, cursor],
    queryFn: () =>
      client.listCalls(integrationKey, {
        limit: PAGE_SIZE,
        cursor,
        ...(status === '' ? {} : { status }),
      }),
  });

  const rows = calls.data?.data ?? [];
  const hasNext = calls.data?.page.nextCursor != null;
  const hasPrevious = cursors.length > 1;

  return (
    <Card>
      <CardHeader
        as="h2"
        title="Activity"
        subtitle="Every call we made to this vendor. Counts and sizes only, never the contents."
        actions={
          <Field label="Outcome" className="min-w-[160px]">
            <Select
              value={status}
              onChange={(event) => {
                setStatus(event.currentTarget.value as IntegrationCallStatus | '');
                setCursors([null]);
              }}
            >
              <option value="">All outcomes</option>
              {INTEGRATION_CALL_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {CALL_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
          </Field>
        }
      />

      <div className="px-6 pt-5 pb-6 max-[480px]:px-4">
        {calls.isPending ? (
          <LoadingBlock label="Loading activity" rows={4} />
        ) : calls.isError ? (
          <Alert tone="error" title="The call log could not be loaded.">
            {calls.error instanceof Error ? calls.error.message : 'Try again in a moment.'}
          </Alert>
        ) : (
          <>
            <Table
              caption="Vendor calls"
              rows={rows}
              getRowKey={(row) => row.id}
              empty={
                <EmptyState
                  title="No calls yet"
                  description={
                    status === ''
                      ? 'Calls appear here as soon as the clinic uses this integration.'
                      : 'No calls with that outcome in this period.'
                  }
                />
              }
              columns={[
                {
                  key: 'startedAt',
                  header: 'When',
                  render: (row: IntegrationCall) => (
                    <time dateTime={row.startedAt} className="whitespace-nowrap tabular-nums">
                      {formatMoment(row.startedAt)}
                    </time>
                  ),
                },
                {
                  key: 'operation',
                  header: 'Operation',
                  render: (row) => <span className="whitespace-nowrap">{row.operation}</span>,
                },
                {
                  key: 'status',
                  header: 'Outcome',
                  render: (row) => (
                    <Badge tone={CALL_STATUS_TONES[row.status]}>
                      {CALL_STATUS_LABELS[row.status]}
                    </Badge>
                  ),
                },
                {
                  key: 'latencyMs',
                  header: 'Took',
                  numeric: true,
                  render: (row) => formatLatency(row.latencyMs),
                },
                {
                  key: 'errorClass',
                  header: 'Error class',
                  render: (row) =>
                    row.errorClass === null ? (
                      <span className="text-neutral-400-text">—</span>
                    ) : (
                      <span className="text-danger-ink">{row.errorClass}</span>
                    ),
                },
              ]}
            />

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-sm text-neutral-500">
                Page {String(cursors.length)}
                {calls.isFetching ? ' · loading…' : ''}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasPrevious}
                  onClick={() => {
                    setCursors((current) => (current.length > 1 ? current.slice(0, -1) : current));
                  }}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={!hasNext}
                  onClick={() => {
                    const next = calls.data?.page.nextCursor;
                    if (next == null) return;
                    setCursors((current) => [...current, next]);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
